import { Component, inject, OnInit, signal } from '@angular/core';

import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NzModalRef, NZ_MODAL_DATA } from 'ng-zorro-antd/modal';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzMessageService } from 'ng-zorro-antd/message';
import { TranslatePipe } from '@ngx-translate/core';
import { User, toCalendarDate, fromCalendarDate } from '@beautyapp/core';
import { VisitAddFacade } from '@beautyapp/shared';

@Component({
  selector: 'beautyapp-user-update-modal',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NzFormModule,
    NzInputModule,
    NzButtonModule,
    NzSelectModule,
    NzDatePickerModule,
    NzSwitchModule,
    NzIconModule,
    NzDividerModule,
    NzSpinModule,
    TranslatePipe
],
  template: `
    <div class="user-update-modal">
      <nz-spin [nzSpinning]="loading()">
        <form nz-form [formGroup]="updateForm" [nzLayout]="'vertical'">
          <div class="form-section">
            <h4 class="section-title">
              <i nz-icon nzType="user" nzTheme="outline"></i>
              {{ 'users.form.personalInfo' | translate }}
            </h4>

            <div nz-row [nzGutter]="16">
              <div nz-col [nzSpan]="12">
                <nz-form-item>
                  <nz-form-label nzRequired>{{ 'users.form.firstName' | translate }}</nz-form-label>
                  <nz-form-control nzErrorTip="{{ 'users.form.firstNameRequired' | translate }}">
                    <input
                      nz-input
                      formControlName="firstname"
                      [placeholder]="'users.form.firstName' | translate"
                    />
                  </nz-form-control>
                </nz-form-item>
              </div>

              <div nz-col [nzSpan]="12">
                <nz-form-item>
                  <nz-form-label>{{ 'users.form.lastName' | translate }}</nz-form-label>
                  <nz-form-control>
                    <input
                      nz-input
                      formControlName="lastname"
                      [placeholder]="'users.form.lastName' | translate"
                    />
                  </nz-form-control>
                </nz-form-item>
              </div>
            </div>

            <div nz-row [nzGutter]="16">
              <div nz-col [nzSpan]="12">
                <nz-form-item>
                  <nz-form-label nzRequired>{{ 'users.form.mobilePhone' | translate }}</nz-form-label>
                  <nz-form-control nzErrorTip="{{ 'users.form.phoneRequired' | translate }}">
                    <input
                      nz-input
                      formControlName="mobilePhone"
                      [placeholder]="'users.form.mobilePhone' | translate"
                    />
                  </nz-form-control>
                </nz-form-item>
              </div>

              <div nz-col [nzSpan]="12">
                <nz-form-item>
                  <nz-form-label>{{ 'users.form.email' | translate }}</nz-form-label>
                  <nz-form-control nzErrorTip="{{ 'users.form.emailInvalid' | translate }}">
                    <input
                      nz-input
                      formControlName="email"
                      type="email"
                      [placeholder]="'users.form.email' | translate"
                    />
                  </nz-form-control>
                </nz-form-item>
              </div>
            </div>

            <div nz-row [nzGutter]="16">
              <div nz-col [nzSpan]="12">
                <nz-form-item>
                  <nz-form-label>{{ 'users.form.cardNo' | translate }}</nz-form-label>
                  <nz-form-control>
                    <input
                      nz-input
                      formControlName="cardNo"
                      [placeholder]="'users.form.cardNo' | translate"
                    />
                  </nz-form-control>
                </nz-form-item>
              </div>

              <div nz-col [nzSpan]="12">
                <nz-form-item>
                  <nz-form-label>{{ 'users.form.birthDate' | translate }}</nz-form-label>
                  <nz-form-control>
                    <nz-date-picker
                      formControlName="birthDate"
                      [nzPlaceHolder]="'users.form.birthDate' | translate"
                    ></nz-date-picker>
                  </nz-form-control>
                </nz-form-item>
              </div>
            </div>

            <nz-form-item>
              <nz-form-label>{{ 'users.form.gender' | translate }}</nz-form-label>
              <nz-form-control>
                <nz-select
                  formControlName="gender"
                  [nzPlaceHolder]="'users.form.selectGender' | translate"
                >
                  <nz-option nzValue="MALE" nzLabel="{{ 'users.form.male' | translate }}"></nz-option>
                  <nz-option nzValue="FEMALE" nzLabel="{{ 'users.form.female' | translate }}"></nz-option>
                  <nz-option nzValue="OTHER" nzLabel="{{ 'users.form.other' | translate }}"></nz-option>
                </nz-select>
              </nz-form-control>
            </nz-form-item>
          </div>

          <nz-divider></nz-divider>

          <div class="form-section">
            <h4 class="section-title">
              <i nz-icon nzType="home" nzTheme="outline"></i>
              {{ 'users.form.addressInfo' | translate }}
            </h4>

            <nz-form-item>
              <nz-form-label>{{ 'users.form.address' | translate }}</nz-form-label>
              <nz-form-control>
                <textarea
                  nz-input
                  formControlName="address"
                  [rows]="3"
                  [placeholder]="'users.form.address' | translate"
                ></textarea>
              </nz-form-control>
            </nz-form-item>
          </div>

          <nz-divider></nz-divider>

          <div class="form-section">
            <h4 class="section-title">
              <i nz-icon nzType="setting" nzTheme="outline"></i>
              {{ 'users.form.preferences' | translate }}
            </h4>

            <div nz-row [nzGutter]="16">
              <div nz-col [nzSpan]="12">
                <nz-form-item>
                  <nz-form-label>{{ 'users.form.receiveNotifications' | translate }}</nz-form-label>
                  <nz-form-control>
                    <nz-switch formControlName="receiveNotifications"></nz-switch>
                  </nz-form-control>
                </nz-form-item>
              </div>

              <div nz-col [nzSpan]="12">
                <nz-form-item>
                  <nz-form-label>{{ 'users.form.receivePromotions' | translate }}</nz-form-label>
                  <nz-form-control>
                    <nz-switch formControlName="receivePromotions"></nz-switch>
                  </nz-form-control>
                </nz-form-item>
              </div>
            </div>
          </div>

          <div class="form-actions">
            <button
              nz-button
              nzType="default"
              (click)="cancel()"
              [disabled]="loading()"
            >
              {{ 'common.cancel' | translate }}
            </button>
            <button
              nz-button
              nzType="primary"
              (click)="saveUser()"
              [nzLoading]="loading()"
              [disabled]="updateForm.invalid"
            >
              <i nz-icon nzType="save" nzTheme="outline"></i>
              {{ 'common.save' | translate }}
            </button>
          </div>
        </form>
      </nz-spin>
    </div>
  `,
  styles: [`
    .user-update-modal {
      .form-section {
        margin-bottom: 24px;

        .section-title {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 16px;
          font-size: 16px;
          font-weight: 600;
          color: #262626;

          i {
            color: #1890ff;
          }
        }
      }

      .form-actions {
        display: flex;
        justify-content: flex-end;
        gap: 12px;
        margin-top: 24px;
        padding-top: 16px;
        border-top: 1px solid #f0f0f0;
      }

      nz-form-item {
        margin-bottom: 16px;
      }

      nz-form-label {
        font-weight: 500;
      }

      .ant-divider {
        margin: 24px 0;
      }
    }
  `]
})
export class UserUpdateModalComponent implements OnInit {
  private fb = inject(FormBuilder);
  private modal = inject(NzModalRef);
  private data = inject(NZ_MODAL_DATA);
  private visitAddFacade = inject(VisitAddFacade);
  private message = inject(NzMessageService);

  loading = signal(false);
  user: User = this.data?.user || {};

  updateForm: FormGroup = this.fb.group({
    firstname: [this.user.firstname || '', [Validators.required]],
    lastname: [this.user.lastname || ''],
    mobilePhone: [this.user.mobilePhone || '', [Validators.required]],
    email: [this.user.email || '', [Validators.email]],
    cardNo: [this.user.cardNo || ''],
    // beaut-525: `new Date('2001-07-31')` is UTC midnight and renders as the
    // 30th west of UTC; the helper builds local midnight.
    birthDate: [fromCalendarDate(this.user.birthDate)],
    gender: [this.user.gender || null],
    address: [this.user.address || ''],
    // receiveNotifications: [this.user.receiveNotifications ?? true],
    // receivePromotions: [this.user.receivePromotions ?? false]
  });

  ngOnInit() {
    // Pre-populate form with user data
    if (this.user.id) {
      this.updateForm.patchValue({
        firstname: this.user.firstname,
        lastname: this.user.lastname,
        mobilePhone: this.user.mobilePhone,
        email: this.user.email,
        cardNo: this.user.cardNo,
        birthDate: fromCalendarDate(this.user.birthDate),
        gender: this.user.gender,
        address: this.user.address,
        // receiveNotifications: this.user.receiveNotifications ?? true,
        // receivePromotions: this.user.receivePromotions ?? false
      });
    }
  }

  saveUser() {
    if (this.updateForm.valid) {
      this.loading.set(true);

      const formValue = this.updateForm.value;
      const updatedUser: User = {
        ...this.user,
        ...formValue,
        // beaut-525: `toISOString()` moves a local-midnight Date back a day
        // for anyone east of UTC — 6 September picked in Tbilisi was sent as
        // the 5th.
        birthDate: toCalendarDate(formValue.birthDate),
        fullName: `${formValue.firstname} ${formValue.lastname || ''}`.trim()
      };

      this.visitAddFacade.updateUser(updatedUser).subscribe({
        next: (result) => {
          this.loading.set(false);
          this.message.success('User updated successfully');
          this.modal.close(result);
        },
        error: (error) => {
          this.loading.set(false);
          this.message.error('Failed to update user');
          console.error('Update error:', error);
        }
      });
    } else {
      // Mark all fields as touched to show validation errors
      Object.keys(this.updateForm.controls).forEach(key => {
        this.updateForm.get(key)?.markAsTouched();
      });
    }
  }

  cancel() {
    this.modal.close(null);
  }
}
